import { createEmptyCondition, createEmptyImGroup, IM_GROUP_LOGIC } from './accrualsConfigurationConstants';
import { createEmptyRuleMeta } from './accrualsRulesBuilderUtils';

function toGroupLogic(value, fallback = IM_GROUP_LOGIC.AND) {
  const raw = String(value || fallback).toUpperCase();
  return raw === IM_GROUP_LOGIC.OR ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND;
}

function isLeafCondition(node) {
  return Boolean(node && (node.field || node.op || node.operator));
}

function toUiCondition(condition, index, groupIndex) {
  return {
    id: condition.id || `condition-${groupIndex}-${index}`,
    field: condition.field || '',
    operator: condition.operator || condition.op || 'eq',
    value: condition.value === null || condition.value === undefined ? '' : String(condition.value),
  };
}

export function syncEstimationGroupConnectors(groups = [], connectors = [], fallbackLogic = IM_GROUP_LOGIC.AND) {
  const needed = Math.max(0, groups.length - 1);
  const fallback = toGroupLogic(fallbackLogic);
  const next = [];
  for (let i = 0; i < needed; i += 1) {
    const existing = connectors[i];
    next.push(existing === IM_GROUP_LOGIC.OR || existing === IM_GROUP_LOGIC.AND ? existing : fallback);
  }
  const groupLogic = next.length && next.every((item) => item === next[0])
    ? next[0]
    : (next[0] || fallback);
  return { groupConnectors: next, groupLogic };
}

export function flattenEstimationGroups(groups = [], groupConnectors = []) {
  const conditions = [];
  const connectors = [];
  (groups || []).forEach((group, groupIdx) => {
    const rows = group?.conditions || [];
    rows.forEach((row, condIdx) => {
      if (conditions.length) {
        if (condIdx === 0 && groupIdx > 0) {
          connectors.push(toGroupLogic(groupConnectors[groupIdx - 1]).toLowerCase());
        } else {
          connectors.push(toGroupLogic(group.logic).toLowerCase());
        }
      }
      conditions.push(row);
    });
  });
  return { conditions, connectors };
}

export function wrapConditionsAsGroups(conditions = [], connectors = []) {
  const rows = Array.isArray(conditions) && conditions.length
    ? conditions
    : [createEmptyCondition(0)];
  const allOr = Boolean(connectors.length)
    && connectors.every((item) => String(item).toUpperCase() === IM_GROUP_LOGIC.OR);
  const group = createEmptyImGroup(0);
  return [{
    ...group,
    logic: allOr ? IM_GROUP_LOGIC.OR : IM_GROUP_LOGIC.AND,
    conditions: rows,
  }];
}

function nodeToGroup(node, index) {
  const children = node.children || node.conditions || [];
  return {
    id: node.id || `group-tree-${index}`,
    logic: toGroupLogic(node.type === 'ANY' || node.type === 'OR' ? IM_GROUP_LOGIC.OR : node.logic),
    conditions: children.filter(isLeafCondition).map((child, condIdx) => toUiCondition(child, condIdx, index)),
  };
}

export function groupsFromConditionTree(tree) {
  if (!tree || typeof tree !== 'object') return null;
  const children = tree.children || tree.conditions || [];
  if (!children.length) return null;

  if (children.every(isLeafCondition)) {
    const group = nodeToGroup(tree, 0);
    if (!group.conditions.length) return null;
    return {
      groups: [group],
      groupConnectors: [],
      groupLogic: group.logic,
    };
  }

  const groupChildren = children.filter((child) => !isLeafCondition(child));
  const allGroupsOfLeaves = groupChildren.length === children.length
    && groupChildren.every((child) => {
      const nested = child.children || child.conditions || [];
      return nested.length && nested.every(isLeafCondition);
    });
  if (!allGroupsOfLeaves) return null;

  const groups = groupChildren.map((child, index) => nodeToGroup(child, index));
  const groupLogic = toGroupLogic(tree.type === 'ANY' || tree.type === 'OR' ? IM_GROUP_LOGIC.OR : tree.logic);
  return {
    groups,
    ...syncEstimationGroupConnectors(groups, groups.slice(0, -1).map(() => groupLogic), groupLogic),
  };
}

export function groupsToConditionTree(groups = [], groupConnectors = []) {
  const groupNodes = (groups || [])
    .map((group) => {
      const children = (group.conditions || []).map((condition) => ({
        field: condition.field,
        op: condition.operator || condition.op,
        value: condition.value,
      }));
      if (!children.length) return null;
      return {
        type: toGroupLogic(group.logic) === IM_GROUP_LOGIC.OR ? 'ANY' : 'ALL',
        children,
      };
    })
    .filter(Boolean);

  if (!groupNodes.length) return { type: 'ALL', children: [] };
  if (groupNodes.length === 1) return groupNodes[0];

  const { groupConnectors: conns } = syncEstimationGroupConnectors(groups, groupConnectors);
  if (conns.every((item) => item === conns[0])) {
    return {
      type: conns[0] === IM_GROUP_LOGIC.OR ? 'ANY' : 'ALL',
      children: groupNodes,
    };
  }

  let tree = groupNodes[0];
  for (let i = 0; i < conns.length; i += 1) {
    tree = {
      type: conns[i] === IM_GROUP_LOGIC.OR ? 'ANY' : 'ALL',
      children: [tree, groupNodes[i + 1]],
    };
  }
  return tree;
}

export function resolveEstimationGroups(rule = {}, fallbackConditions = []) {
  const existing = Array.isArray(rule.groups) ? rule.groups.filter((group) => group && Array.isArray(group.conditions)) : [];
  if (existing.length) {
    const groups = existing.map((group, index) => ({
      id: group.id || `group-${index}`,
      logic: toGroupLogic(group.logic),
      conditions: group.conditions.length ? group.conditions : [createEmptyCondition(0)],
    }));
    const synced = syncEstimationGroupConnectors(groups, rule.groupConnectors, rule.groupLogic);
    const flattened = flattenEstimationGroups(groups, synced.groupConnectors);
    return { groups, ...synced, ...flattened };
  }

  const fromTree = groupsFromConditionTree(rule.condition_tree || rule.conditionTree);
  if (fromTree?.groups?.length) {
    const flattened = flattenEstimationGroups(fromTree.groups, fromTree.groupConnectors);
    return { ...fromTree, ...flattened };
  }

  const groups = wrapConditionsAsGroups(
    Array.isArray(rule.conditions) && rule.conditions.length ? rule.conditions : fallbackConditions,
    rule.ruleMeta?.connectors || rule.connectors || [],
  );
  const synced = syncEstimationGroupConnectors(groups, [], rule.groupLogic);
  const flattened = flattenEstimationGroups(groups, synced.groupConnectors);
  return { groups, ...synced, ...flattened };
}

export function estimationGroupsPatch(groups, groupConnectors, groupLogic) {
  const synced = syncEstimationGroupConnectors(groups, groupConnectors, groupLogic);
  const flattened = flattenEstimationGroups(groups, synced.groupConnectors);
  return {
    groups,
    groupLogic: synced.groupLogic,
    groupConnectors: synced.groupConnectors,
    conditions: flattened.conditions,
    ruleMeta: {
      ...createEmptyRuleMeta(flattened.conditions.length),
      connectors: flattened.connectors,
    },
  };
}
